import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { IndividualService } from '../../../Component/Api/Individual/IndividualService';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import AppCheckbox from '../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { genderList, provinceList, raceList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { EE123PeopleRole_Edit, EE123PeopleRole_View } from '../../../Component/Common/Constants/UserRoles';
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { Individual } from '../../../Component/Model/Dto/Individual';
import { userRolesState } from '../../../State/user.Atom';
import { CustomDialogTitle } from '../Custom/CustomDialogTitle';
import { EmploymentDataTable } from './EmploymentData/EmploymentDataTable';
import { individualSchema } from './IndividualValidation';
import { getDateWithoutTime } from "../../../Helpers/DateFormatter";

interface IProps {
  id: number;
  scorecardId: string;
  showIndividualModal: boolean;
  closeIndividualModal: () => void;
}

export const IndividualModal = (props: IProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const userRoles = useRecoilValue(userRolesState);
  const { control, reset, handleSubmit, formState: { isSubmitting }, watch, trigger } = useForm({
    mode: 'all',
    resolver: yupResolver(individualSchema)
  });

  const identificationNumber = watch('identificationNumber');
  const employeeCode = watch('employeeCode');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const editRoleAccess = [
    EE123PeopleRole_Edit
  ]
  const isEditable = userRoles.some(r => editRoleAccess.includes(r));

  const getEmployeeData = async (id: number) => {
    if (id === undefined || id === 0) return;
    try {
      const request: Individual.GetRequest = {
        id: id,
        scorecardId: props.scorecardId,
        LinkedType: undefined,
        InterventionId: undefined,
        filterVAStartDate: undefined,
        filterVAEndDate: undefined
      }
      setIsLoading(true);
      const result = await IndividualService.GetIndividualByID(request);
      reset(result.data as Individual.IndividualDto);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }

  }

  const handleDataSubmit = async (data: FieldValues) => {
    setIsSaving(true);
    try {
      const finalData = data as Individual.IndividualUpdateDto;
      finalData.SampledIndividualViewModel = null;
      
      const result = await IndividualService.SaveIndividual(finalData);
      toast.success(`Employee updated successfully`);
      props.closeIndividualModal();
    } 
    catch (error: any) {
      console.log(error);
      error.forEach((errorMessage: string) => {
        toast.error(errorMessage);
      });
    } 
    finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    if (identificationNumber) {
      trigger('employeeCode');
    } else if (employeeCode) {
      trigger('identificationNumber');
    }
  }, [identificationNumber, employeeCode, trigger]);
  
  useEffect(() => {
    getEmployeeData(props.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showIndividualModal])

  return (
    <Dialog disableEnforceFocus open={props.showIndividualModal} onClose={() => { props.closeIndividualModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
      <CustomDialogTitle id="individual-dialog-title" onClose={() => props.closeIndividualModal()}>
        {isEditable ? 'View/Edit Employee Details' : 'View Employee Details'}
      </CustomDialogTitle>
      <DialogContent>
        {isLoading && <SkeletonLoading formLoading FieldAmount={6} />}
        {!isLoading &&
          <Box>
            <AppAccordion expandOnDefault={true} label={'Employee Data'}>
              <form id='IndividualForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} mt={1}>
                    <AppTextInput control={control} name="identificationNumber" label="ID Number" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="employeeCode" label="Employee Code" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="firstName" label="Name" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppTextInput control={control} name="lastName" label="Surname" disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="gender" label="Gender" items={genderList} disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="raceId" label="Race" items={raceList} disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppSelectList control={control} name="province" label="Province" items={provinceList} disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppDatepicker control={control} name="dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' maxDate={getDateWithoutTime(new Date())} disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='disabled' label='Disabled' disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='isYes' label='Y.E.S. Employee' disabled={!isEditable} />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <AppCheckbox control={control} name='isYesAbsorbed' label='Y.E.S. Absorbed' disabled={!isEditable} />
                  </Grid>
                </Grid>
              </form>
            </AppAccordion>
            <AppAccordion expandOnDefault={false} label='Employment Data'>
              <EmploymentDataTable scorecardId={props.scorecardId} individualId={props.id} />
            </AppAccordion>
          </Box >
        }
      </DialogContent>
      <DialogActions>
        {isEditable && <LoadingButton loading={isSubmitting} variant='contained' form='IndividualForm' type="submit" color='primary' disabled={isSaving || !userRoles.includes(EE123PeopleRole_View) || !userRoles.includes(EE123PeopleRole_Edit)}>
          Save
        </LoadingButton>}
      </DialogActions>
    </Dialog>
  )
}
